import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatExpansionPanel, MatExpansionPanelDescription, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatActionList, MatList, MatListItem } from '@angular/material/list';

import { BaasI18n } from '@lvflanders/bar';
import { BaasRemark, BaasRemarkList, BarI18nPipe } from '@lvflanders/bar-ng-ui';

import { DidoDocumentenService } from '../../services/dido-documenten.service';
import { DocumentUploadModel } from '../../types/dido-document-upload-model.type';
import { DocumenttypeModel } from '../../types/documenttype-model.type';
import { DidoDocumentenControlUpload } from '../dido-documenten-control-upload/dido-documenten-control-upload';
import { DidoDocumentenGallery } from '../dido-documenten-gallery/dido-documenten-gallery';
import { DidoDocumentenControlDialogInternDetail } from './detail/dialog-intern-detail';
import { DidoDocumentenControlDialogInternEmailMeta } from './email-metadata/dialog-intern-email-meta';

//  NOTE for typescript VS code validation
const UNDEFINED_DOCUMENT_UPLOAD_MODEL = undefined as unknown as DocumentUploadModel;

export interface DidoDocumentenControlDialogDataIntern {
    documenten: DocumentUploadModel[];
    documenttypes: DocumenttypeModel[];
    maxFileSize?: number;
    werktInDossierContext: boolean;
}

/** Dialog for the documenten control to modify or set the metadata. */
@Component({
    selector: 'dido-documenten-control-dialog-intern',
    templateUrl: './dido-documenten-control-dialog-intern.component.html',
    styleUrls: ['./dido-documenten-control-dialog-intern.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatExpansionPanelDescription,
        MatActionList,
        MatListItem,
        NgClass,
        MatList,
        DidoDocumentenControlDialogInternEmailMeta,
        DidoDocumentenControlUpload,
        MatCheckbox,
        ReactiveFormsModule,
        FormsModule,
        MatIconButton,
        MatIcon,
        BaasRemarkList,
        BaasRemark,
        DidoDocumentenControlDialogInternDetail,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        BarI18nPipe
    ]
})
export class DidoDocumentenControlDialogInternComponent {
    // data given to current dialog
    readonly data = inject<DidoDocumentenControlDialogDataIntern>(MAT_DIALOG_DATA);
    readonly #i18n = inject(BaasI18n);
    // reference to current dialog
    readonly #dialogRef = inject(MatDialogRef<DidoDocumentenControlDialogInternComponent>);
    readonly #dialog = inject(MatDialog);
    readonly #documentservice = inject(DidoDocumentenService);
    // the documenten to add or edit
    readonly documenten: DocumentUploadModel[];
    // the title bovenaan the dialog
    readonly title: string;
    // the document types for the dropdown
    readonly documenttypes: DocumenttypeModel[];
    // whether the ok button has been clicked
    readonly confirmed = signal(false);
    // the currently selected document
    readonly selectedDocument = signal<DocumentUploadModel>(UNDEFINED_DOCUMENT_UPLOAD_MODEL);
    readonly isDocSelected = signal(true);
    //if the checkbox is selected
    readonly applyAllSelected = signal(false);
    // points to the bijlagen selector
    readonly selectedBijlage = signal<DocumentUploadModel>(UNDEFINED_DOCUMENT_UPLOAD_MODEL);
    // Whether all validations are met (including from other documenten)
    readonly allValidationsNotMet = signal<boolean>(false);
    #dialogRefGallery: MatDialogRef<DidoDocumentenGallery> | undefined;

    constructor() {
        // assign the data
        this.documenten = this.data.documenten;
        this.documenttypes = this.data.documenttypes;
        // set title based on update or insert and on the number of documenten
        this.title = this.#i18n.get(
            this.documenten.length > 1 ? 'DOMAIN_DIDO.DOCUMENTEN_CONTROL.ADD_DOCUMENTEN' : 'DOMAIN_DIDO.DOCUMENTEN_CONTROL.ADD_DOCUMENT'
        );
        this.selectedDocument.set(this.documenten[0]);
    }

    selectedBijlageChange(changes) {
        this.isDocSelected.set(false);
        this.selectedBijlage.set(changes);
    }

    selectedDocumentChange(change) {
        this.isDocSelected.set(true);
        if (this.selectedBijlage()) {
            this.selectedBijlage.set(UNDEFINED_DOCUMENT_UPLOAD_MODEL);
        }
        this.selectedDocument.set(change);
    }

    /** Occurs when the ok button is clicked. */
    confirm(): void {
        if (this.applyAllSelected()) {
            this.documenten.forEach((document) => {
                if (document.isMailBestand && document.emailBijlages) {
                    document.emailBijlages.forEach((bijlage) => {
                        bijlage.documenttypeCode = this.selectedDocument().documenttypeCode;
                        bijlage.koppelenHuidigDossier = this.selectedDocument().koppelenHuidigDossier;
                        bijlage.dossiersAanmaken = this.selectedDocument().dossiersAanmaken;
                        bijlage.takenAanmaken = this.selectedDocument().takenAanmaken;
                    });
                }
                if (!document.isMailBestand) {
                    document.status = this.selectedDocument().status;
                }
                document.documenttypeCode = this.selectedDocument().documenttypeCode;
                document.koppelenHuidigDossier = this.selectedDocument().koppelenHuidigDossier;
                document.dossiersAanmaken = this.selectedDocument().dossiersAanmaken;
                document.takenAanmaken = this.selectedDocument().takenAanmaken;
            });
        }
        this.confirmed.set(true);
        const invalid = this.#checkInvalid();
        this.allValidationsNotMet.set(invalid);
        if (!invalid) {
            this.#dialogRef.close(this.documenten);
        }
    }

    bijlagePreview(didoDocument: DocumentUploadModel) {
        this.#dialogRefGallery = this.#dialog.open(DidoDocumentenGallery, {
            // pass the data needed
            id: 'galleryDialog',
            position: { top: '0' },
            data: {
                selectedDocument: didoDocument,
                isUploadPreview: true
            },
            minWidth: '85vw',
            panelClass: 'dido-documenten_gallery'
        });
        (document.getElementsByClassName('cdk-overlay-dark-backdrop')[0] as HTMLElement)
            ? ((document.getElementsByClassName('cdk-overlay-dark-backdrop')[0] as HTMLElement).style.background = 'rgba(0, 0, 0, 0.850)')
            : '';
        this.#dialogRefGallery.afterClosed().subscribe(() => {
            this.#dialogRefGallery = undefined;
        });
    }

    bijlageVerwijderen(bijlage: DocumentUploadModel, document: DocumentUploadModel) {
        const i = document.emailBijlages.indexOf(bijlage);
        document.emailBijlages.splice(i, 1);
        document.bijlagesAangepast = true;
    }

    onUploaded(responses: DocumentUploadModel[], document: DocumentUploadModel): void {
        const models: DocumentUploadModel[] = [];

        responses.forEach((response) => {
            this.#documentservice.enrichDocument(response);
            // push it the model
            models.push(response);
        });

        document.bijlagesAangepast = true;
        this.#handleAddDocumenten(models);
    }

    /**
     * Checks whether state is invalid.
     * @param documentNotToCheck
     */
    #checkInvalid(): boolean {
        const docsInValid = this.documenten.some((document) => document.documenttypeCode == null || document.documenttypeCode.length == 0);
        const bijlagenInValid = this.#checkBijlagenInValid();
        return docsInValid || bijlagenInValid;
    }

    #checkBijlagenInValid(): boolean {
        return this.documenten.some((doc) => {
            if (doc.isMailBestand && doc.emailBijlages && doc.emailBijlages.length > 0 && doc.uitpakken) {
                return doc.emailBijlages.some((document) => document.documenttypeCode == null || document.documenttypeCode.length == 0);
            } else return false;
        });
    }

    #handleAddDocumenten(documenten: DocumentUploadModel[]): void {
        documenten.forEach((document) => {
            // insert it at the front of the array
            // document.isMailbijlage = true;
            this.selectedDocument().emailBijlages.unshift(document);
        });
    }
}
